import { z } from "zod";
import { DateOnly, ItemId, MilestoneStatus, RelationKind } from "./items.ts";
import { KEY_PATTERN } from "./keys.ts";
import { ContentLanguage } from "./translation.ts";

export const SpaceKey = z
  .string()
  .trim()
  .transform((v) => v.toUpperCase())
  .pipe(z.string().regex(KEY_PATTERN));

export const SpaceCreate = z.object({
  key: SpaceKey,
  name: z.string().trim().min(1).max(200),
  description: z.string().max(10_000).nullish(),
  icon: z.string().max(64).nullish(),
  color: z.string().max(32).nullish(),
  /** Workflow of the auto-created space-wide product; defaults to the built-in one. */
  workflowId: z.uuid().optional(),
  /** Content language of the space-wide product; defaults to the instance default locale. */
  contentLanguage: ContentLanguage.optional(),
});
export type SpaceCreate = z.input<typeof SpaceCreate>;

export const ProductCreate = z.object({
  spaceId: z.uuid(),
  key: SpaceKey,
  name: z.string().trim().min(1).max(200),
  description: z.string().max(10_000).nullish(),
  icon: z.string().max(64).nullish(),
  color: z.string().max(32).nullish(),
  visibility: z.enum(["space", "private"]).optional(),
  workflowId: z.uuid().optional(),
  /** Language the product's items are written in; defaults to the instance default locale. */
  contentLanguage: ContentLanguage.optional(),
});
export type ProductCreate = z.input<typeof ProductCreate>;

/**
 * Inline edits of a product (PATCH /products/:key). `key` is validated by the mutation (422 `invalid_key`,
 * `key_taken`, `space_product`), so the client can show a specific reason.
 */
export const ProductPatch = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  description: z.string().max(10_000).nullable().optional(),
  key: z.string().trim().max(40).optional(),
});
export type ProductPatch = z.infer<typeof ProductPatch>;

/** Inline edits of a space (PATCH /spaces/:key); a key change renames the space-wide product with it. */
export const SpacePatch = ProductPatch;
export type SpacePatch = z.infer<typeof SpacePatch>;

/**
 * Per-space overrides of instance defaults (`spaces.settings`). A missing key inherits the instance setting.
 * - `contentLanguage`, `productVisibility`: defaults for products created in the space.
 * - `translation`: machine translation of the space's items, comments and notes. `enabled: false` switches it off
 *   for the space; a space cannot switch it on while the instance has it off. The auto switches override the
 *   instance ones.
 */
export const SpaceSettings = z.object({
  contentLanguage: ContentLanguage.optional(),
  productVisibility: z.enum(["space", "private"]).optional(),
  translation: z
    .object({
      enabled: z.boolean().optional(),
      autoTranslate: z.boolean().optional(),
      autoTranslateNotes: z.boolean().optional(),
    })
    .strict()
    .optional(),
});
export type SpaceSettings = z.infer<typeof SpaceSettings>;

/** PUT /spaces/:key/settings: keys left out stay as they are, null goes back to inheriting. */
export const SpaceSettingsPatch = z
  .object({
    contentLanguage: ContentLanguage.nullable(),
    productVisibility: z.enum(["space", "private"]).nullable(),
    translation: z
      .object({
        enabled: z.boolean().nullable(),
        autoTranslate: z.boolean().nullable(),
        autoTranslateNotes: z.boolean().nullable(),
      })
      .partial()
      .strict(),
  })
  .partial()
  .strict();
export type SpaceSettingsPatch = z.infer<typeof SpaceSettingsPatch>;

/** Applies a patch; null removes the override. Pure, so the client can preview what it sends. */
export function applySpaceSettingsPatch(current: SpaceSettings, patch: SpaceSettingsPatch): SpaceSettings {
  const next: SpaceSettings = { ...current, translation: { ...current.translation } };
  const set = <T extends object, K extends keyof T>(target: T, key: K, value: T[K] | null | undefined) => {
    if (value === undefined) return;
    if (value === null) delete target[key];
    else target[key] = value;
  };
  set(next, "contentLanguage", patch.contentLanguage);
  set(next, "productVisibility", patch.productVisibility);
  const t = next.translation!;
  set(t, "enabled", patch.translation?.enabled);
  set(t, "autoTranslate", patch.translation?.autoTranslate);
  set(t, "autoTranslateNotes", patch.translation?.autoTranslateNotes);
  if (Object.keys(t).length === 0) delete next.translation;
  return next;
}

export const MilestoneCreate = z.object({
  productId: z.uuid(),
  name: z.string().trim().min(1).max(200),
  version: z.string().max(64).nullish(),
  description: z.string().max(50_000).nullish(),
  targetDate: DateOnly.nullish(),
});
export type MilestoneCreate = z.infer<typeof MilestoneCreate>;

export const MilestonePatch = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  version: z.string().max(64).nullable().optional(),
  description: z.string().max(50_000).nullable().optional(),
  targetDate: DateOnly.nullable().optional(),
  status: MilestoneStatus.optional(),
});
export type MilestonePatch = z.infer<typeof MilestonePatch>;

export const CommentCreate = z.object({ itemId: ItemId, bodyMd: z.string().trim().min(1).max(100_000) });
export type CommentCreate = z.infer<typeof CommentCreate>;

export const RelationCreate = z.object({ fromId: ItemId, toId: ItemId, kind: RelationKind });
export type RelationCreate = z.infer<typeof RelationCreate>;
